import { useCallback, useContext, useEffect, useState } from 'react';

import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { getCurrentTab } from 'lib/chromeScripting/util';
import { newQuotingPortal } from 'lib/hosts/quoting/interface';
import { Undef } from 'types/UtilityTypes';
import captureException from 'utils/captureException';

// True when the sidepanel's attached Chrome tab URL matches a quoting portal.
// Used to hide draft-email / batch-quote UI on bidding portals (ENG-8031).
// Re-resolves when tabId changes or the attached tab's URL updates so
// navigating portal -> inbox in the same tab does not leave hide state stale.
export function useIsOnQuotingPortal(): boolean {
  const {
    currentState: { tabId },
  } = useContext(SidebarStateContext);
  const [isOnQuotingPortal, setIsOnQuotingPortal] = useState(false);

  const resolveAttachedTab = useCallback(async (): Promise<
    Undef<chrome.tabs.Tab>
  > => {
    if (tabId && tabId > 0) {
      try {
        const tab = await chrome.tabs.get(tabId);
        if (tab?.id) {
          return tab;
        }
      } catch (error) {
        // Tab may have closed or the extension context may be invalidated;
        // fall open to the active tab so hide state can still clear.
        captureException(error, {
          functionName: 'useIsOnQuotingPortal.resolveAttachedTab',
        });
        return getCurrentTab();
      }
    }

    return getCurrentTab();
  }, [tabId]);

  useEffect(() => {
    if (typeof chrome === 'undefined' || !chrome.runtime) {
      setIsOnQuotingPortal(false);
      return;
    }

    let cancelled = false;

    const refresh = async () => {
      const tab = await resolveAttachedTab();
      if (cancelled) {
        return;
      }

      setIsOnQuotingPortal(!!newQuotingPortal(tab?.url));
    };

    void refresh();

    // Only listen when the sidepanel is bound to a known tab; otherwise a
    // one-shot getCurrentTab resolve above is enough.
    if (!(tabId && tabId > 0) || !chrome.tabs?.onUpdated) {
      return () => {
        cancelled = true;
      };
    }

    const onTabUpdated = (
      updatedTabId: number,
      changeInfo: { url?: string; status?: string }
    ) => {
      if (updatedTabId !== tabId) {
        return;
      }

      // URL change is the primary signal; also refresh on complete in case
      // changeInfo.url was omitted for same-document navigations.
      if (changeInfo.url == null && changeInfo.status !== 'complete') {
        return;
      }

      void refresh();
    };

    chrome.tabs.onUpdated.addListener(onTabUpdated);

    return () => {
      cancelled = true;
      chrome.tabs.onUpdated.removeListener(onTabUpdated);
    };
  }, [resolveAttachedTab, tabId]);

  return isOnQuotingPortal;
}
